/* MatchAllLinesMode.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const RegexUtils = require("../RegexUtils");
const TOKEN_COLORS = require("../Tokens");
const FRAME_SIZE = 10000; //number of lines processed at once
const MAX_LINES_IN_ONE_MATCH = 100;
const STRATEGY_NO_WEBWORKER = "no_webworker";
const STRATEGY_NO_WEBWORKER_MAX_LINES = 3000;
const STRATEGY_USE_WEBWORKER_AND_ADD_ALL_MARKERS = "webworker_and_add_all_markers";
const STRATEGY_USE_WEBWORKER_AND_ADD_ALL_MARKERS_MAX_LINES = 100000;
const STRATEGY_USE_WEBWORKER_AND_ADD_MARKERS_IN_VISIBLE_FRAMES =
    "webworker_and_add_markers_in_visible_frames";
const ZERO_NAMEDGROUP_TOKENS = ["ace_matched_section1_marker", "ace_matched_section2_marker"];
const AceUtils = require("../AceUtils");

class MatchAllLinesMode extends EventEmitter {
    constructor(regexEditor) {
        super();
        this._regexEditor = regexEditor;
        this._regexEditorSession = this._regexEditor.getSession();
        this._strategy = STRATEGY_NO_WEBWORKER; // used to decide an approach to get better UI responsiveness
        this._frames = [];
        this._visibleFrames = [];
        this._renderingInProgress = false;
    }

    addMarkers(regex, namedGroupsList) {
        if (this._renderingInProgress) {
            return;
        }
        this.clear();
        if (!regex || this._isDocumentEmpty()) {
            return;
        }

        this._namedGroupsList = namedGroupsList;
        let lineCount = this._regexEditorSession.getDocument().getLength();
        if (lineCount <= STRATEGY_NO_WEBWORKER_MAX_LINES) {
            this._strategy = STRATEGY_NO_WEBWORKER;
            this._addMarkersSimple(regex, this._namedGroupsList);
        } else {
            if (lineCount <= STRATEGY_USE_WEBWORKER_AND_ADD_ALL_MARKERS_MAX_LINES) {
                this._strategy = STRATEGY_USE_WEBWORKER_AND_ADD_ALL_MARKERS;
            } else {
                this._strategy = STRATEGY_USE_WEBWORKER_AND_ADD_MARKERS_IN_VISIBLE_FRAMES;
            }
            //depending on strategy above.. further processing chooses to add markers in visible frames or in all frames

            this._renderingInProgress = true;
            this.emit("InProgress", this._renderingInProgress);
            this._addMarkersUsingWebWorker(regex);
        }
    }

    _addMarkersUsingWebWorker(regex) {
        this._frames = this.getFrames();
        if (_.isEmpty(this._frames)) {
            return;
        }
        //initialize web worker to find matches in text
        this._initializeWebWorker();
        this._webWorker.onmessage = this._processMessageFromWebWorker =
            this._processMessageFromWebWorker.bind(this);
        //sending frames to web worker for processing
        this._webWorker.postMessage({frames: this._frames, regex: regex, frameSize: FRAME_SIZE});
    }

    _initializeWebWorker() {
        if (this._webWorker) {
            return;
        }

        let blob = new Blob([
            `
            self.onmessage = function(event) {
                let frames = event.data.frames;
                let regexObj = new RegExp(event.data.regex, 'gm');
                let prevFrameLastMatch = null, prevFrame = null;
                let maxMatchesInBatch = 1000;
                let prevFrameUnmatchedTextLastIndex = null;
            
                for(let i=0; i<frames.length; i++) {
                    let currentFrame = frames[i];
                    if(prevFrameUnmatchedTextLastIndex) {
                        currentFrame.startIndex = prevFrameUnmatchedTextLastIndex;
                        currentFrame.text = prevFrame.text.substring(prevFrameUnmatchedTextLastIndex - prevFrame.startIndex) + currentFrame.text;
                    }
                    let currMatch = null, prevMatch = null;
                    let matches = [];
                    while((currMatch = regexObj.exec(currentFrame.text)) !== null) {
                        currMatch.input = '';
                        if(currMatch[0].length > 0) {
                            matches.push(currMatch);
                        }
                        if(prevMatch && currMatch.index === prevMatch.index) {
                            regexObj.lastIndex++;
                        }
                        prevMatch = currMatch;
                    }
                    currentFrame.matches = matches;
                    currFrameLastMatch = currentFrame.matches.length === 0 ? null : currentFrame.matches[currentFrame.matches.length - 1];
                    if(currFrameLastMatch && currFrameLastMatch.index + currentFrame.startIndex + currFrameLastMatch[0].length > currentFrame.lastHundredthLineIndex) {
                        prevFrameUnmatchedTextLastIndex = currFrameLastMatch.index + currentFrame.startIndex + currFrameLastMatch[0].length;
                    } else {
                        prevFrameUnmatchedTextLastIndex = currentFrame.lastHundredthLineIndex;
                    }
                    currentFrame.lastIndex = prevFrameUnmatchedTextLastIndex -1;
                    prevFrame = currentFrame;
                }
                self.postMessage({processingComplete: {frames: frames, regex: event.data.regex}});
            }
        `,
        ]);
        let blobURL = window.URL.createObjectURL(blob);
        this._webWorker = new Worker(blobURL);
    }

    getFrames() {
        let document = this._regexEditorSession.getDocument();
        let lastLine = document.getLength();
        if (lastLine === 1 && document.getValue().length === 0) {
            return [];
        }
        let currentFrame = null,
            frameCount = 0,
            frames = [];
        do {
            let currentFrameStartRow = frameCount * FRAME_SIZE;
            let currentFrameEndRow =
                currentFrameStartRow + FRAME_SIZE > lastLine
                    ? lastLine - 1
                    : currentFrameStartRow + FRAME_SIZE - 1;
            currentFrame = {
                range: AceUtils.getRangeInstance(
                    currentFrameStartRow,
                    0,
                    currentFrameEndRow,
                    document.getLine(currentFrameEndRow).length
                ),
            };
            currentFrame.startIndex = document.positionToIndex({
                row: currentFrameStartRow,
                column: 0,
            });
            currentFrame.endIndex = document.positionToIndex({
                row: currentFrame.range.end.row,
                column: currentFrame.range.end.column,
            });
            currentFrame.lastHundredthLine =
                currentFrame.range.end.row - MAX_LINES_IN_ONE_MATCH >= 0
                    ? currentFrame.range.end.row - MAX_LINES_IN_ONE_MATCH
                    : null;
            currentFrame.lastHundredthLineIndex = document.positionToIndex({
                row: currentFrame.lastHundredthLine,
                column: 0,
            });
            currentFrame.text = document.getTextRange(currentFrame.range);
            if (currentFrame.range.end !== lastLine - 1) {
                currentFrame.text += "\n"; //adding line break for continuity in matching text across frames
            }
            currentFrame.matches = [];
            frames.push(currentFrame);
            frameCount++;
        } while (currentFrame.range.end.row < lastLine - 1);
        return frames;
    }

    _processMessageFromWebWorker(event) {
        if (event.data.processingComplete) {
            let frames = event.data.processingComplete.frames;
            let document = this._regexEditorSession.getDocument();
            for (let i = 0, prevFrame = null; i < frames.length; i++) {
                let currFrame = frames[i];
                let range = {start: currFrame.range.start, end: currFrame.range.end};
                if (currFrame.startIndex !== this._frames[i].startIndex) {
                    range.start = document.indexToPosition(
                        prevFrame.range.end.column + 1,
                        prevFrame.range.end.row
                    );
                }
                if (currFrame.endIndex !== this._frames[i].endIndex) {
                    if (currFrame.endIndex === currFrame.lastHundredthLineIndex) {
                        range.end = {row: currFrame.lastHundredthLine, column: 0};
                    } else {
                        range.end = document.indexToPosition(
                            currFrame.endIndex -
                                currFrame.startIndex +
                                currFrame.range.start.column,
                            currFrame.range.start.end
                        );
                    }
                }
                currFrame.range = AceUtils.getRangeInstance(
                    range.start.row,
                    range.start.column,
                    range.end.row,
                    range.end.column
                );
                this._frames[i].range = currFrame.range;
                prevFrame = currFrame;
            }
            this._frames = frames;
            if (this._strategy === STRATEGY_USE_WEBWORKER_AND_ADD_ALL_MARKERS) {
                this._addMarkersInAllFrames();
            } else {
                if (!this._addMarkersInVisibleFramesDebounce) {
                    this._regexEditorSession.addEventListener(
                        "changeScrollTop",
                        (this._addMarkersInVisibleFramesDebounce = _.debounce(
                            this._addMarkersInVisibleFrames.bind(this)
                        )),
                        100
                    );
                    this._regexEditor.commands.addEventListener(
                        "afterExec",
                        (this._onEditorCommand = this._onEditorCommand.bind(this))
                    );
                }
                this._visibleFrames = [];
                this._addMarkersInVisibleFrames();
            }
            this._renderingInProgress = false;
            this.emit("InProgress", this._renderingInProgress);
        }
    }

    _addMarkersInAllFrames() {
        let zeroNamedGroupsTokens = ZERO_NAMEDGROUP_TOKENS;
        for (let index = 0; index < this._frames.length; index++) {
            if (_.isEmpty(this._namedGroupsList)) {
                zeroNamedGroupsTokens =
                    index > 0 && this._frames[index - 1].matches.length % 2 === 1
                        ? zeroNamedGroupsTokens.slice().reverse()
                        : zeroNamedGroupsTokens;
            }
            this._addMarkersInFrame(
                this._frames[index],
                this._namedGroupsList,
                zeroNamedGroupsTokens
            );
        }
    }

    _addMarkersInVisibleFrames() {
        if (_.isEmpty(this._frames)) {
            return;
        }
        let firstRow = this._regexEditor.getFirstVisibleRow();
        let lastRow = this._regexEditor.getLastVisibleRow();
        if (!_.isEmpty(this._visibleFrames)) {
            if (
                this._visibleFrames[0].range.compare(firstRow, 0) === 0 &&
                this._visibleFrames[this._visibleFrames.length - 1].range.compare(lastRow, 0) === 0
            ) {
                return;
            }
        }
        this._visibleFrames = [];
        this.removeAllMarkers();
        let firstFrameIndex = this._getFrameIndex(this._frames, firstRow);
        // new rows are being processed & not ready yet to add markers
        if (firstFrameIndex === -1) {
            return;
        }
        this._visibleFrames.push(this._frames[firstFrameIndex]);
        this._addMarkersInFrame(
            this._visibleFrames[0],
            this._namedGroupsList,
            ZERO_NAMEDGROUP_TOKENS
        );
        if (this._visibleFrames[0].range.compare(lastRow, 0) !== 0) {
            this._visibleFrames.push(this._frames[firstFrameIndex + 1]);
            let zeroNamedGroupsTokens =
                this._visibleFrames[0].matches.length % 2 === 0
                    ? ZERO_NAMEDGROUP_TOKENS
                    : ZERO_NAMEDGROUP_TOKENS.slice().reverse();
            this._addMarkersInFrame(
                this._visibleFrames[1],
                this._namedGroupsList,
                zeroNamedGroupsTokens
            );
        }
    }

    _getFrameIndex(frames, rowNumber) {
        if (rowNumber < 0 || _.isEmpty(frames)) {
            return -1;
        }
        let frameStartLine = Math.floor(rowNumber / FRAME_SIZE) * FRAME_SIZE;
        let frameStartIndex = frameStartLine / FRAME_SIZE;
        if (frameStartIndex >= frames.length) {
            frameStartIndex = -1;
        } else {
            let compareResult = frames[frameStartIndex].range.compare(rowNumber, 0);
            frameStartIndex += compareResult;
        }
        return frameStartIndex;
    }

    _addMarkersInFrame(frame, namedGroupsList, zeroNamedGroupsTokens) {
        if (!frame) {
            return;
        }
        zeroNamedGroupsTokens = !zeroNamedGroupsTokens
            ? ZERO_NAMEDGROUP_TOKENS
            : zeroNamedGroupsTokens;
        let document = this._regexEditorSession.getDocument();
        let matches = frame.matches;
        let textRange = frame.range;
        let prevMatch = null;
        if (document.positionToIndex(textRange.start) !== frame.startIndex) {
            textRange.start = document.indexToPosition(frame.startIndex);
        }
        for (let index = 0; index < matches.length; index++) {
            let match = matches[index];
            let namedGroupsDetails = RegexUtils.getNamedGroupDetails(
                match,
                namedGroupsList,
                TOKEN_COLORS
            );
            let matchStartPosition;
            if (prevMatch) {
                matchStartPosition = this._regexEditorSession
                    .getDocument()
                    .indexToPosition(
                        match.index - prevMatch.match.index + prevMatch.range.start.column,
                        prevMatch.range.start.row
                    );
            } else {
                matchStartPosition = this._regexEditorSession
                    .getDocument()
                    .indexToPosition(match.index + textRange.start.column, textRange.start.row);
            }
            let matchEndPosition = this._regexEditorSession
                .getDocument()
                .indexToPosition(
                    matchStartPosition.column + match[0].length,
                    matchStartPosition.row
                );
            let range = AceUtils.getRangeInstance(
                matchStartPosition.row,
                matchStartPosition.column,
                matchEndPosition.row,
                matchEndPosition.column
            );
            if (_.isEmpty(namedGroupsDetails)) {
                let token = zeroNamedGroupsTokens[index % 2];
                this._regexEditorSession.addMarker(range, token, "text", false);
            } else {
                for (let i = 0; i < namedGroupsDetails.length; i++) {
                    if (namedGroupsDetails[i].value) {
                        let namedGroupStartPosition = this._regexEditorSession
                            .getDocument()
                            .indexToPosition(
                                namedGroupsDetails[i].startIndex + matchStartPosition.column,
                                matchStartPosition.row
                            );
                        let namedGroupEndPosition = this._regexEditorSession
                            .getDocument()
                            .indexToPosition(
                                namedGroupsDetails[i].startIndex +
                                    matchStartPosition.column +
                                    namedGroupsDetails[i].value.length,
                                matchStartPosition.row
                            );
                        let token = "ace_" + namedGroupsDetails[i].token + "_marker";
                        let range = AceUtils.getRangeInstance(
                            namedGroupStartPosition.row,
                            namedGroupStartPosition.column,
                            namedGroupEndPosition.row,
                            namedGroupEndPosition.column
                        );
                        this._regexEditorSession.addMarker(range, token, "text", false);
                    }
                }
                prevMatch = {match: match, range: range};
            }
        }
    }

    _addMarkersSimple(regex, namedGroupsList) {
        if (!regex) {
            return;
        }
        let regexObj = new RegExp(regex, "gm");
        let currMatch,
            prevMatch = null;
        let matchDetails = [];
        let zeroNamedGroupTokens = ZERO_NAMEDGROUP_TOKENS.reverse();
        let editorText = this._regexEditorSession.getValue();
        while ((currMatch = regexObj.exec(editorText)) !== null) {
            let namedGroupsDetails = RegexUtils.getNamedGroupDetails(
                currMatch,
                namedGroupsList,
                TOKEN_COLORS
            );
            let matchStartPosition = this._regexEditorSession
                .getDocument()
                .indexToPosition(currMatch.index, 0);
            let matchEndPosition = this._regexEditorSession
                .getDocument()
                .indexToPosition(
                    matchStartPosition.column + currMatch[0].length,
                    matchStartPosition.row
                );
            let matchRange = AceUtils.getRangeInstance(
                matchStartPosition.row,
                matchStartPosition.column,
                matchEndPosition.row,
                matchEndPosition.column
            );
            currMatch.input = "";
            if (!_.isEmpty(currMatch[0])) {
                matchDetails.push(currMatch);
                if (_.isEmpty(namedGroupsDetails)) {
                    let token = zeroNamedGroupTokens[matchDetails.length % 2];
                    this._regexEditorSession.addMarker(matchRange, token, "text", false);
                } else {
                    for (let i = 0; i < namedGroupsDetails.length; i++) {
                        if (namedGroupsDetails[i].value) {
                            let namedGroupStartPosition = this._regexEditorSession
                                .getDocument()
                                .indexToPosition(
                                    namedGroupsDetails[i].startIndex + matchStartPosition.column,
                                    matchStartPosition.row
                                );
                            let namedGroupEndPosition = this._regexEditorSession
                                .getDocument()
                                .indexToPosition(
                                    namedGroupsDetails[i].startIndex +
                                        matchStartPosition.column +
                                        namedGroupsDetails[i].value.length,
                                    matchStartPosition.row
                                );
                            let token = "ace_" + namedGroupsDetails[i].token + "_marker";
                            let range = AceUtils.getRangeInstance(
                                namedGroupStartPosition.row,
                                namedGroupStartPosition.column,
                                namedGroupEndPosition.row,
                                namedGroupEndPosition.column
                            );
                            this._regexEditorSession.addMarker(range, token, "text", false);
                        }
                    }
                }
            }
            if (prevMatch && currMatch.index === prevMatch.index) {
                regexObj.lastIndex++;
            }
            prevMatch = currMatch;
        }
        let lastRow = this._regexEditorSession.getDocument().getLength() - 1;
        this._frames = [
            {
                matches: matchDetails,
                range: AceUtils.getRangeInstance(
                    0,
                    0,
                    lastRow,
                    this._regexEditorSession.getDocument().getLine(lastRow).length
                ),
            },
        ];
    }

    _onEditorCommand(event) {
        let commandName = _.get(event, "command.name", "");
        if (commandName === "gotostart") {
            this.removeAllMarkers();
            let frame = this._frames[0];
            this._addMarkersInFrame(frame, this._namedGroupsList);
            this._visibleFrames = [frame];
        } else if (commandName === "gotoend") {
            this.removeAllMarkers();
            let frame = this._frames[this._frames.length - 1];
            this._addMarkersInFrame(frame, this._namedGroupsList);
            this._visibleFrames = [frame];
        }
    }

    removeAllMarkers() {
        let existingMarkers = this._regexEditorSession.getMarkers(false);
        if (_.isEmpty(existingMarkers)) {
            return;
        }
        Object.keys(existingMarkers).forEach(function (existingMarker) {
            let marker = existingMarkers[existingMarker];
            if (
                marker.clazz.startsWith("ace_namedgroup") ||
                marker.clazz.startsWith("ace_matched_section")
            ) {
                this.removeMarker(existingMarker);
            }
        }, this._regexEditorSession);
    }

    getMatchDetailsAtPosition(position) {
        if (_.isEmpty(this._frames)) {
            return;
        }
        for (let frameIndex = 0; frameIndex < this._frames.length; frameIndex++) {
            if (this._frames[frameIndex].range.contains(position.row, position.column)) {
                let currentFrame = this._frames[frameIndex];
                let prevMatchDetails = {match: {index: 0}, range: currentFrame.range};
                for (let matchIndex = 0; matchIndex < currentFrame.matches.length; matchIndex++) {
                    let match = currentFrame.matches[matchIndex];
                    let matchStartPosition;
                    matchStartPosition = this._regexEditorSession
                        .getDocument()
                        .indexToPosition(
                            match.index -
                                prevMatchDetails.match.index +
                                prevMatchDetails.range.start.column,
                            prevMatchDetails.range.start.row
                        );
                    let matchEndPosition = this._regexEditorSession
                        .getDocument()
                        .indexToPosition(
                            matchStartPosition.column + match[0].length,
                            matchStartPosition.row
                        );
                    let range = AceUtils.getRangeInstance(
                        matchStartPosition.row,
                        matchStartPosition.column,
                        matchEndPosition.row,
                        matchEndPosition.column
                    );
                    if (range.contains(position.row, position.column)) {
                        return {match: match, range: range};
                    }
                    prevMatchDetails = {match: match, range: range};
                }
            }
        }
        return null;
    }

    _isEndOfDocument(row, column) {
        let document = this._regexEditorSession.getDocument();
        let lastLine = document.getLength() > 0 ? document.getLength() - 1 : document.getLength();
        let lastColumn = document.getLine(lastLine).length;
        return row > lastLine || (row === lastLine && column >= lastColumn);
    }

    _isDocumentEmpty() {
        let lineCount = this._regexEditorSession.getDocument().getLength();
        return (
            lineCount === 0 ||
            (lineCount === 1 && this._regexEditorSession.getDocument().getValue().length === 0)
        );
    }

    /**
     * Delete all markers
     * Removes event listeners
     * Clears all instance level variables
     */
    clear() {
        this.removeAllMarkers();
        this._frames = [];
        this._visibleFrames = [];
        this._strategy = STRATEGY_NO_WEBWORKER;
        this._renderingInProgress = false;

        if (this._addMarkersInVisibleFramesDebounce) {
            this._regexEditorSession.removeListener(
                "changeScrollTop",
                this._addMarkersInVisibleFramesDebounce
            );
            this._addMarkersInVisibleFramesDebounce = null;
        }
        this._regexEditor.commands.removeListener("afterExec", this._onEditorCommand);
    }

    /**
     * Clears the markers & instance level variables
     * Terminates web worker if any
     */
    remove() {
        this.clear();
        if (this._webWorker) {
            this._webWorker.terminate();
            this._webWorker = null;
        }
    }

    getMatchCount() {
        if (_.isEmpty(this._frames)) {
            return 0;
        } else {
            return this._frames.reduce(function (matchCount, currentFrame) {
                return currentFrame.matches.length + matchCount;
            }, 0);
        }
    }

    getCurrentMatchNumber(endPosition) {
        if (_.isEmpty(this._frames)) {
            return 0;
        } else {
            let document = this._regexEditorSession.getDocument();
            let frameContainingEndPositionRow = this._getFrameIndex(this._frames, endPosition.row);
            let matchCount = this._frames
                .slice(0, frameContainingEndPositionRow)
                .reduce(function (matchCount, currentFrame) {
                    return currentFrame.matches.length + matchCount;
                }, 0);
            let currentFrame = this._frames[frameContainingEndPositionRow];
            matchCount += this._getMatchesInAFrameBeforeStartOfGivenRange(
                endPosition,
                currentFrame,
                document
            );
            let nextFrameIndex =
                frameContainingEndPositionRow < this._frames.length - 1
                    ? frameContainingEndPositionRow + 1
                    : null;
            // consider matches in next frame if next frame range overlaps with frame Containing End PositionRow
            if (nextFrameIndex && this._frames[nextFrameIndex].startIndex) {
                matchCount += this._getMatchesInAFrameBeforeStartOfGivenRange(
                    endPosition,
                    this._frames[nextFrameIndex],
                    document
                );
            }
            return matchCount;
        }
    }

    _getMatchesInAFrameBeforeStartOfGivenRange(endPosition, currentFrame, document) {
        let prevMatch;
        let inputRange = AceUtils.getRangeInstance(
            endPosition.row,
            endPosition.column,
            endPosition.row,
            endPosition.column
        );
        let frameRange = currentFrame.range;
        if (
            currentFrame.startIndex &&
            document.positionToIndex(frameRange.start) !== currentFrame.startIndex
        ) {
            frameRange.start = document.indexToPosition(currentFrame.startIndex);
        }
        // if input range is after current frame start - return zero matches
        if (inputRange.compare(frameRange.start.row, frameRange.start.column) !== -1) {
            return 0;
        }
        //find last match index which falls after input range end position
        let index = currentFrame.matches.findIndex(function (currentMatch) {
            let matchStartPosition;
            if (prevMatch) {
                matchStartPosition = document.indexToPosition(
                    currentMatch.index - prevMatch.match.index + prevMatch.range.start.column,
                    prevMatch.range.start.row
                );
            } else {
                matchStartPosition = document.indexToPosition(
                    currentMatch.index + frameRange.start.column,
                    frameRange.start.row
                );
            }
            let matchEndPosition = document.indexToPosition(
                matchStartPosition.column + currentMatch[0].length,
                matchStartPosition.row
            );
            let matchRange = AceUtils.getRangeInstance(
                matchStartPosition.row,
                matchStartPosition.column,
                matchEndPosition.row,
                matchEndPosition.column
            );
            prevMatch = {match: currentMatch, range: matchRange};
            return inputRange.compare(matchStartPosition.row, matchStartPosition.column) > -1;
        });
        // if index is -1 (no matches found) hence all matches in current frame start before input range
        // in all other cases -> index represents # of matches starting before input range
        return index === -1 ? currentFrame.matches.length : index;
    }

    getNextMatchNumber(totalCount, currentMatchNumber, currentMatchNumberStale) {
        if (totalCount === 0) {
            return 0;
        }
        let selectionRange = this._regexEditor.getSelectionRange();
        if (currentMatchNumberStale && totalCount > 1) {
            currentMatchNumber = this.getCurrentMatchNumber(selectionRange.end);
        }
        let currentFrameIndex = this._getFrameIndex(this._frames, selectionRange.end.row);
        let nextMatchNumber;
        let crossedEndOnce = false;
        // look for next match starting with current frame.
        //move to next frame if you can't find next match
        // if reaching end of last frame, move to first frame if moving to first frame has not happened before
        let prevMatch = null;
        let document = this._regexEditorSession.getDocument();
        while (!nextMatchNumber && currentFrameIndex < this._frames.length) {
            let currentFrame = this._getFrame(currentFrameIndex, this._frames, document);
            let nextMatch = currentFrame.matches.find(function (currentMatch) {
                let matchRange = this._getMatchRangeFromIndex(
                    currentMatch,
                    currentFrame.range,
                    prevMatch,
                    document
                );
                let currentMatchStartsAfterEndOfSelection = selectionRange.isEmpty()
                    ? selectionRange.compare(matchRange.start.row, matchRange.start.column) >= 0
                    : selectionRange.compareEnd(matchRange.start.row, matchRange.start.column) ===
                      1;
                if (currentMatchStartsAfterEndOfSelection) {
                    this._regexEditor.revealRange(matchRange);
                    return true;
                }
                prevMatch = {match: currentMatch, range: matchRange};
            }, this);
            if (nextMatch) {
                nextMatchNumber = crossedEndOnce || totalCount === 1 ? 1 : currentMatchNumber + 1;
            }
            currentFrameIndex++;
            prevMatch = null;
            if (!crossedEndOnce && currentFrameIndex === this._frames.length) {
                crossedEndOnce = true;
                currentFrameIndex = 0;
                selectionRange.setStart({row: 0, column: 0});
                selectionRange.setEnd({row: 0, column: 0});
            }
        }
        return nextMatchNumber;
    }

    getPreviousMatchNumber(totalCount, currentMatchNumber, currentMatchNumberStale) {
        let selectionRange = this._regexEditor.getSelectionRange();
        let matchCountTillSelectionRangeStart = currentMatchNumber;
        if (currentMatchNumberStale && totalCount > 1) {
            matchCountTillSelectionRangeStart = this.getCurrentMatchNumber(selectionRange.start);
            matchCountTillSelectionRangeStart =
                matchCountTillSelectionRangeStart > 0
                    ? matchCountTillSelectionRangeStart + 1
                    : matchCountTillSelectionRangeStart;
        }
        let frameIndex;
        let previousMatchNumber;
        let crossedEndOnce = false;
        let document = this._regexEditorSession.getDocument();
        let selectionRangeStartIndex = document.positionToIndex(selectionRange.start, 0);
        if (matchCountTillSelectionRangeStart === 0) {
            frameIndex = this._frames.length - 1;
            crossedEndOnce = true;
            selectionRangeStartIndex = document.positionToIndex(this._frames[frameIndex].range.end);
        } else {
            frameIndex = this._getFrameIndexConsiderOverlappingFrames(
                this._frames,
                selectionRange.start,
                document
            );
        }
        while (!previousMatchNumber && frameIndex >= 0) {
            let currentFrame = this._getFrame(frameIndex, this._frames, document);
            let currentFrameStartIndex = currentFrame.startIndex
                ? currentFrame.startIndex
                : document.positionToIndex(currentFrame.range.start);
            let prevMatch;
            for (let index = currentFrame.matches.length - 1; index >= 0; index--) {
                let currentMatch = currentFrame.matches[index];
                if (selectionRangeStartIndex > currentMatch.index + currentFrameStartIndex) {
                    prevMatch = currentMatch;
                    break;
                }
            }
            if (prevMatch) {
                let matchRange = this._getMatchRangeFromIndex(
                    prevMatch,
                    currentFrame.range,
                    null,
                    document
                );
                this._regexEditor.revealRange(matchRange);
                previousMatchNumber =
                    crossedEndOnce || totalCount === 1
                        ? totalCount
                        : matchCountTillSelectionRangeStart - 1;
            } else {
                frameIndex--;
                if (!crossedEndOnce && frameIndex < 0) {
                    frameIndex = this._frames.length - 1;
                    crossedEndOnce = true;
                    selectionRangeStartIndex = document.positionToIndex(
                        this._frames[frameIndex].range.end
                    );
                }
            }
        }
        return previousMatchNumber;
    }

    _getFrameIndexConsiderOverlappingFrames(frames, position, document) {
        let frameIndex = this._getFrameIndex(frames, position.row);
        if (frameIndex + 1 < frames.length && frames[frameIndex + 1].startIndex) {
            let nextFrame = frames[frameIndex + 1];
            let nextFrameRange = nextFrame.range;
            if (document.positionToIndex(nextFrameRange.start) !== nextFrame.startIndex) {
                nextFrameRange.start = document.indexToPosition(nextFrame.startIndex);
            }
            // if position is after current frame start
            if (AceUtils.comparePoints(position, nextFrameRange.start) > 0) {
                frameIndex = frameIndex + 1;
            }
        }
        return frameIndex;
    }

    _getFrame(frameIndex, frames, document) {
        let currentFrame = frames[frameIndex];
        let frameRange = currentFrame.range;
        if (
            currentFrame.startIndex &&
            document.positionToIndex(frameRange.start) !== currentFrame.startIndex
        ) {
            frameRange.start = document.indexToPosition(currentFrame.startIndex);
        }
        return currentFrame;
    }

    _getMatchRangeFromIndex(currentMatch, frameRange, prevMatch, document) {
        let matchStartPosition;
        if (prevMatch) {
            matchStartPosition = document.indexToPosition(
                currentMatch.index - prevMatch.match.index + prevMatch.range.start.column,
                prevMatch.range.start.row
            );
        } else {
            matchStartPosition = document.indexToPosition(
                currentMatch.index + frameRange.start.column,
                frameRange.start.row
            );
        }
        let matchEndPosition = document.indexToPosition(
            matchStartPosition.column + currentMatch[0].length,
            matchStartPosition.row
        );
        return AceUtils.getRangeInstance(
            matchStartPosition.row,
            matchStartPosition.column,
            matchEndPosition.row,
            matchEndPosition.column
        );
    }
}

module.exports = MatchAllLinesMode;
